// First-login "Welcome to ArcLine" tour — a short, skippable modal wizard
// orienting a brand-new (or newly-migrated) user to the app's shape before
// they're on their own. Mirrors ConfirmDialog.tsx's overlay/backdrop/Escape
// convention. Shown once per user — Shell.tsx decides *whether* to render
// this at all (based on GET /api/me/tour-seen) and marks it seen on finish
// or skip; this component only owns the slide-stepping UI.
import { useEffect, useState } from "react";
import { c, font } from "../lib/theme";

interface Slide { title: string; body: string; }

const SLIDES: Slide[] = [
  {
    title: "Welcome to ArcLine",
    body: "ArcLine tracks every company and federal opportunity your firm is working, from first contact through a signed contract — and, further out, through a company's eventual public listing. This is a 30-second tour of where things live.",
  },
  {
    title: "Deal Flow",
    body: "Opportunities and Companies are your two rosters. Company Scoreboard ranks companies by readiness, and the Match Workspace shows which opportunities and companies the system thinks fit — you confirm the ones that make sense.",
  },
  {
    title: "The Pipeline",
    body: "Every active deal moves through a 12-stage process on the Pipeline Board, from first sourced to signed. Some stages need a person to approve the move before it can continue — those show up in your Actions list.",
  },
  {
    title: "Company Readiness",
    body: "Before a company can take on U.S. federal work, it has to get legally and operationally ready — onboarding, standup, proposals, and capital all live under Company Readiness in the sidebar.",
  },
  {
    title: "If you get stuck",
    body: "Every page has an OVERVIEW button in the header explaining what it's for. The Copilot chat (bottom-right) can answer questions in plain English. Your Dashboard also has a short getting-started checklist for your first real tasks.",
  },
];

export function WelcomeTour({ onDone }: { onDone: () => void }) {
  const [i, setI] = useState(0);
  const last = i === SLIDES.length - 1;

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") onDone(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onDone]);

  const slide = SLIDES[i];
  return (
    <div onClick={onDone} style={{ position: "fixed", inset: 0, background: "rgba(3,5,11,0.72)", display: "grid", placeItems: "center", zIndex: 100 }}>
      <div role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}
        style={{ width: 460, maxWidth: "90vw", background: c.soft.card, borderRadius: 18, padding: 28, boxShadow: "0 24px 64px rgba(0,0,0,0.55)" }}>
        <div style={{ display: "flex", gap: 6, marginBottom: 18 }}>
          {SLIDES.map((_, n) => (
            <div key={n} style={{ flex: 1, height: 4, borderRadius: 2, background: n <= i ? c.soft.accent : c.soft.cardHi }} />
          ))}
        </div>
        <div style={{ font: `800 22px ${font.display}`, color: c.textHi, marginBottom: 10 }}>{slide.title}</div>
        <div style={{ font: `400 15.5px ${font.sans}`, color: c.muted2, lineHeight: 1.6, minHeight: 84 }}>{slide.body}</div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 22 }}>
          <button onClick={onDone} style={{ font: `600 13px ${font.sans}`, color: c.muted, background: "transparent", border: "none", cursor: "pointer", padding: "8px 4px" }}>Skip</button>
          <div style={{ display: "flex", gap: 8 }}>
            {i > 0 && (
              <button onClick={() => setI((n) => n - 1)} style={{ font: `600 13px ${font.sans}`, color: c.muted2, background: c.soft.cardHi, border: "none", borderRadius: 6, padding: "9px 16px", cursor: "pointer" }}>Back</button>
            )}
            <button onClick={() => (last ? onDone() : setI((n) => n + 1))} style={{ font: `600 13px ${font.sans}`, color: c.onAccent, background: c.soft.accent, border: "none", borderRadius: 6, padding: "9px 18px", cursor: "pointer" }}>
              {last ? "Get started" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
